import { App, Button, Input, Popconfirm, Select, Space, Table, Tag, Tooltip, Typography } from 'antd';
import { Pencil, Plus, RefreshCw, Search, Shield, ShieldCheck, Trash2, User } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiErrorText } from '../../api/api-error-text.js';
import { http } from '../../api/client.js';
import { EmptyState, PageHeader } from '../../components/ui/index.js';
import { CreatePlatformRoleModal } from './CreatePlatformRoleModal.js';
import { PlatformPermissionDetailDrawer } from './PlatformPermissionDetailDrawer.js';
import {
  type AdminRoleRow,
  PLATFORM_ROLE_TYPE_VALUES,
  type PlatformRoleType,
  isPlatformRoleType,
} from './platform-permission-types.js';

interface RoleListResponse {
  rows: AdminRoleRow[];
  total: number;
}

const ROLE_TYPE_META: Record<
  PlatformRoleType,
  { key: string; color: string; icon: React.ReactNode }
> = {
  system: { key: 'system', color: 'red', icon: <Shield size={12} /> },
  admin: { key: 'admin', color: 'orange', icon: <ShieldCheck size={12} /> },
  user: { key: 'user', color: 'blue', icon: <User size={12} /> },
};

const TYPE_FILTERS: Array<{ value: 'all' | PlatformRoleType; key: 'all' | PlatformRoleType }> = [
  { value: 'all', key: 'all' },
  ...PLATFORM_ROLE_TYPE_VALUES.map((value) => ({ value, key: value })),
];

export function PlatformPermissionsPage({ embedded = false }: { embedded?: boolean } = {}) {
  const { message } = App.useApp();
  const { t } = useTranslation('platformPermissions');
  const [roles, setRoles] = useState<AdminRoleRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [openRoleId, setOpenRoleId] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [deletingRoleId, setDeletingRoleId] = useState<string | null>(null);

  const fetchData = useCallback(() => {
    setLoading(true);
    http
      .get<RoleListResponse>('/admin/platform-roles', {
        params: { pageSize: '500', groupByRoot: 'false' },
      })
      .then((response) => {
        setRoles(response.data.rows.filter((row) => isPlatformRoleType(row.roleType)));
      })
      .catch(() => message.error(t('loadFailed')))
      .finally(() => setLoading(false));
  }, [message, t]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const deleteRole = async (role: AdminRoleRow) => {
    setDeletingRoleId(role.roleid);
    try {
      await http.delete(`/admin/platform-roles/${role.roleid}`);
      message.success(t('deleteSuccess', { name: role.name }));
      if (openRoleId === role.roleid) setOpenRoleId(null);
      fetchData();
    } catch (error) {
      message.error(apiErrorText(error, t('deleteFailed')));
    } finally {
      setDeletingRoleId(null);
    }
  };

  const visibleRows = useMemo(() => {
    const query = q.trim().toLowerCase();
    return roles.filter((role) => {
      const matchesType = typeFilter === 'all' || role.roleType === typeFilter;
      const matchesQuery =
        !query ||
        [role.name, role.title, role.description].some((value) =>
          (value ?? '').toLowerCase().includes(query),
        );
      return matchesType && matchesQuery;
    });
  }, [q, roles, typeFilter]);

  return (
    <div>
      <PageHeader
        title={embedded ? t('platformRoles') : t('title')}
        icon={<ShieldCheck size={20} />}
        subtitle={
          roles.length > 0 ? (
            <span>
              {t('totalRoles', { count: roles.length })} ·{' '}
              {TYPE_FILTERS.slice(1).map((type) => {
                const count = roles.filter((role) => role.roleType === type.value).length;
                const meta = ROLE_TYPE_META[type.value as PlatformRoleType];
                return (
                  <span key={type.value} style={{ marginLeft: 8 }}>
                    {t(type.key)} <strong style={{ color: meta.color }}>{count}</strong>
                  </span>
                );
              })}
            </span>
          ) : (
            t('common:loading')
          )
        }
        extra={
          <Space>
            <Input
              allowClear
              placeholder={t('searchPlaceholder')}
              prefix={<Search size={14} />}
              value={q}
              onChange={(event) => setQ(event.target.value)}
              style={{ width: 280 }}
            />
            <Select
              size="small"
              value={typeFilter}
              style={{ width: 120 }}
              onChange={setTypeFilter}
              options={TYPE_FILTERS.map((type) => ({
                value: type.value,
                label: t(type.key),
              }))}
            />
            <Button icon={<RefreshCw size={14} />} onClick={fetchData}>
              {t('common:refresh')}
            </Button>
            <Button type="primary" icon={<Plus size={14} />} onClick={() => setCreateOpen(true)}>
              {t('newRole')}
            </Button>
          </Space>
        }
      />

      <div className="crest-list-card" style={{ padding: 0, overflow: 'hidden' }}>
        <Table
          rowKey="roleid"
          loading={loading}
          dataSource={visibleRows}
          locale={{
            emptyText: (
              <EmptyState
                title={q ? t('noMatches', { query: q }) : t('noRoles')}
                description={q ? t('noMatchesDescription') : t('emptyDescription')}
                compact
              />
            ),
          }}
          pagination={{
            pageSize: 50,
            showSizeChanger: false,
            showTotal: (total) => t('totalRoles', { count: total }),
            position: ['bottomRight'],
            style: { padding: '12px 16px', margin: 0 },
          }}
          columns={[
            {
              title: t('roleName'),
              dataIndex: 'name',
              fixed: 'left',
              width: 300,
              render: (value: string, row) => (
                <Space size={6}>
                  <Typography.Text strong style={{ color: '#1f2937' }}>
                    {value}
                  </Typography.Text>
                  {row.isBuiltin && (
                    <Tooltip title={t('builtinTooltip')}>
                      <Tag color="default" style={{ fontSize: 10 }}>
                        {t('builtin')}
                      </Tag>
                    </Tooltip>
                  )}
                </Space>
              ),
            },
            {
              title: t('type'),
              dataIndex: 'roleType',
              width: 110,
              render: (value: string) => {
                const meta = isPlatformRoleType(value) ? ROLE_TYPE_META[value] : null;
                if (!meta) return <Tag>{value}</Tag>;
                return (
                  <Tag
                    color={meta.color}
                    icon={meta.icon}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}
                  >
                    {t(meta.key)}
                  </Tag>
                );
              },
            },
            {
              title: t('description'),
              dataIndex: 'description',
              ellipsis: true,
              render: (value: string | null, row) =>
                value || row.title || <Typography.Text type="secondary">-</Typography.Text>,
            },
            {
              title: t('members'),
              dataIndex: 'memberCount',
              width: 90,
              align: 'right',
              sorter: (a, b) => a.memberCount - b.memberCount,
              render: (value: number) => (
                <Tag
                  color={value > 0 ? 'blue' : 'default'}
                  style={{ minWidth: 36, textAlign: 'center' }}
                >
                  {value}
                </Tag>
              ),
            },
            {
              title: t('privileges'),
              dataIndex: 'privilegeCount',
              width: 90,
              align: 'right',
              sorter: (a, b) => a.privilegeCount - b.privilegeCount,
              render: (value: number) => (
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  {value.toLocaleString()}
                </Typography.Text>
              ),
            },
            {
              title: 'App',
              dataIndex: 'appCount',
              width: 80,
              align: 'right',
              render: (value: number) => (
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  {value}
                </Typography.Text>
              ),
            },
            {
              title: t('actions'),
              key: '__actions',
              fixed: 'right',
              width: 180,
              render: (_value, row) => (
                <Space size={0} onClick={(event) => event.stopPropagation()}>
                  <Button
                    type="link"
                    size="small"
                    icon={<Pencil size={14} />}
                    onClick={() => setOpenRoleId(row.roleid)}
                  >
                    {t('editRole')}
                  </Button>
                  {!row.isBuiltin && (
                    <Popconfirm
                      title={t('deleteConfirmTitle', { name: row.name })}
                      description={t('deleteConfirmDescription')}
                      okText={t('deleteRole')}
                      cancelText={t('common:cancel')}
                      okButtonProps={{ danger: true }}
                      onConfirm={() => deleteRole(row)}
                    >
                      <Button
                        type="link"
                        size="small"
                        danger
                        icon={<Trash2 size={14} />}
                        loading={deletingRoleId === row.roleid}
                      >
                        {t('deleteRole')}
                      </Button>
                    </Popconfirm>
                  )}
                </Space>
              ),
            },
          ]}
          onRow={(row) => ({
            onClick: () => setOpenRoleId(row.roleid),
            style: { cursor: 'pointer' },
          })}
          scroll={{ x: 'max-content' }}
          sticky
        />
      </div>

      <PlatformPermissionDetailDrawer
        roleId={openRoleId}
        onClose={() => {
          setOpenRoleId(null);
          fetchData();
        }}
        onSaved={fetchData}
      />
      <CreatePlatformRoleModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={(role) => {
          fetchData();
          setOpenRoleId(role.roleid);
        }}
      />
    </div>
  );
}
